import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, Camera, Loader2, X } from "lucide-react";
export default function BarcodeScannerModal({
  isOpen,
  onClose,
  onDetected,
  title = "Scan dengan perangkat ini",
  subtitle = "Barcode akan masuk ke keranjang otomatis",
}) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const detectedRef = useRef(false);
  const onDetectedRef = useRef(onDetected);
  const [status, setStatus] = useState("Menyiapkan kamera...");
  const [error, setError] = useState("");

  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    detectedRef.current = false;
    setStatus("Meminta akses kamera...");
    setError("");

    const startScanner = async () => {
      try {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        if (cancelled) return;

        const reader = new BrowserMultiFormatReader();
        const controls = await reader.decodeFromConstraints(
          {
            audio: false,
            video: {
              facingMode: { ideal: "environment" },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
          },
          videoRef.current,
          (result) => {
            if (cancelled || detectedRef.current || !result) return;

            const code = result.getText()?.trim();
            if (!code) return;

            detectedRef.current = true;
            controlsRef.current?.stop();
            onDetectedRef.current?.(code);
            onClose();
          },
        );

        if (cancelled) {
          controls.stop();
          return;
        }

        controlsRef.current = controls;
        setStatus("Arahkan kamera ke barcode produk");
      } catch (scanError) {
        if (cancelled) return;
        console.error("Camera barcode scanner error", scanError);
        setError(
          scanError?.name === "NotAllowedError"
            ? "Izin kamera ditolak. Izinkan kamera untuk menggunakan scanner perangkat."
            : "Kamera tidak dapat digunakan di perangkat ini.",
        );
        setStatus("Scanner kamera tidak tersedia");
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      controlsRef.current?.stop();
      controlsRef.current = null;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
              <Camera className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900">{title}</h2>
              <p className="text-[11px] text-slate-500">{subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label="Tutup scanner kamera"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="relative aspect-[4/3] bg-slate-950">
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            autoPlay
            muted
            playsInline
          />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-28 w-64 rounded-2xl border-2 border-white/90 shadow-[0_0_0_9999px_rgba(15,23,42,0.32)]" />
          </div>
          {!error && status !== "Arahkan kamera ke barcode produk" && (
            <div className="absolute inset-x-0 bottom-4 flex justify-center">
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-950/70 px-3 py-2 text-xs font-medium text-white">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {status}
              </span>
            </div>
          )}
        </div>

        <div className="px-5 py-4">
          {error ? (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : (
            <p className="text-center text-xs text-slate-500">{status}</p>
          )}
          <button
            type="button"
            onClick={onClose}
            className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-bold text-slate-700 transition-colors hover:bg-slate-50"
          >
            Tutup scanner
          </button>
        </div>
      </div>
    </div>
  );
}
